// 拖 cue 編號放開時的存法（Cue 庫專案，導演頁歌頁和編輯頁共用）。
// 色塊要跟著走：被上一個 cue 夾住的 cue，轉場秒數要一起存成實際走的秒數（算法在 timelineKeys 的 carryCueMove）。
// 後台是兩條路（stage-crossfade 一個一個、stage-time(s) 一次），沒辦法一個交易，所以用可靠的順序：
//   存：先存轉場（到位時間還沒動，轉場 = 實際走的秒數，GO 不變 → 畫面不會跳），再存時間；時間失敗 → 把轉場一個一個退回，不留半套
//   退：先退時間，再退轉場（兩個順序的中間狀態 GO 都一樣）

import { setSlotStageCrossfade, stageSetCueTime, stageSetCueTimes } from './stageApi';

export interface CueMoveChange { cueId: string; videoTimeSec: number; crossfadeSeconds?: number }
export interface SavedCueMove { undo: () => Promise<void>; redo: () => Promise<void> }

type PrevCross = { crossfadeSeconds: number; own: boolean };

async function saveTimes(pid: string, sid: string, changes: readonly CueMoveChange[]): Promise<SavedCueMove> {
  if (changes.length === 1) {
    const c = changes[0];
    const r = await stageSetCueTime(pid, sid, c.cueId, c.videoTimeSec);
    const prev = r.previousTimeSec;
    return {
      undo: async () => { await stageSetCueTime(pid, sid, c.cueId, prev); },
      redo: async () => { await stageSetCueTime(pid, sid, c.cueId, c.videoTimeSec); },
    };
  }
  const times = changes.map(c => ({ cueId: c.cueId, videoTimeSec: c.videoTimeSec }));
  const r = await stageSetCueTimes(pid, sid, times);
  const prev = r.previous;
  return {
    undo: async () => { await stageSetCueTimes(pid, sid, prev); },
    redo: async () => { await stageSetCueTimes(pid, sid, times); },
  };
}

const restoreCross = async (pid: string, sid: string, cueId: string, prev: PrevCross | undefined): Promise<void> => {
  if (prev) await setSlotStageCrossfade(pid, sid, cueId, prev.crossfadeSeconds, { own: prev.own });
};

export async function saveCueMoves(pid: string, sid: string, changes: readonly CueMoveChange[]): Promise<SavedCueMove> {
  const withCross = changes.filter(c => c.crossfadeSeconds !== undefined);
  const done: { cueId: string; seconds: number; prev: PrevCross | undefined }[] = [];
  let times: SavedCueMove;
  try {
    for (const c of withCross) {
      const r = await setSlotStageCrossfade(pid, sid, c.cueId, c.crossfadeSeconds!, { own: true });
      done.push({ cueId: c.cueId, seconds: c.crossfadeSeconds!, prev: r.previous });
    }
    times = await saveTimes(pid, sid, changes);
  } catch (e) {
    for (const d of [...done].reverse()) { try { await restoreCross(pid, sid, d.cueId, d.prev); } catch { /* 退不回去就交給呼叫端重讀 */ } }
    throw e;
  }
  return {
    undo: async () => {
      await times.undo();
      for (const d of [...done].reverse()) await restoreCross(pid, sid, d.cueId, d.prev);
    },
    redo: async () => {
      for (const d of done) await setSlotStageCrossfade(pid, sid, d.cueId, d.seconds, { own: true });
      await times.redo();
    },
  };
}
